import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  TextField,
  Grid,
} from '@mui/material';
import { Edit as EditIcon, Visibility as ViewIcon, Search as SearchIcon, FilterList as FilterIcon, CheckCircle as ApplyIcon, Undo as RevertIcon, Update as UpdateIcon, Settings as SettingsIcon } from '@mui/icons-material';
import api from '../../services/api';

interface AddNameTagRequest {
  id: number;
  requester_name: string;
  requester_email_address: string;
  requester_company_name: string;
  requester_company_website: string;
  is_address_owner: number;
  discovered_address_links: string;
  address: string;
  name_tag: string;
  website: string;
  category_label: string;
  description: string;
  comment: string;
  status: number;
  created_at: string;
  updated_at: string;
}

interface StatusUpdateRequest {
  status: number;
}

const AddNameTagTab: React.FC = () => {
  const [requests, setRequests] = useState<AddNameTagRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');
  const searchTermRef = useRef(searchTerm);

  // Update ref when searchTerm changes
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);
  
  // Status update dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AddNameTagRequest | null>(null);
  const [newStatus, setNewStatus] = useState<number>(0);
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewRequest, setViewRequest] = useState<AddNameTagRequest | null>(null);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRequest, setEditRequest] = useState<AddNameTagRequest | null>(null);
  const [editForm, setEditForm] = useState({
    name_tag: '',
    website: '',
    category_label: '',
    description: '',
    comment: ''
  });

  // Apply confirmation dialog
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyRequest, setApplyRequest] = useState<AddNameTagRequest | null>(null);

  // Revert confirmation dialog
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertRequest, setRevertRequest] = useState<AddNameTagRequest | null>(null);

  const statusLabels = {
    0: { label: 'Pending - Ready to Apply', color: 'warning' as const },
    1: { label: 'In Progress - Ready to Apply', color: 'info' as const },
    2: { label: 'Completed', color: 'success' as const },
    3: { label: 'Rejected', color: 'error' as const },
    4: { label: 'On Hold', color: 'default' as const },
    5: { label: 'Canceled', color: 'default' as const },
  };

  const addressOwnerLabels = {
    0: 'Owner',
    1: 'Not Owner',
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter.toString());
      }
      
      if (searchTermRef.current.trim()) {
        params.append('search', searchTermRef.current.trim());
      }
      
      const response = await api.get(`/api/inquiry/name-tag?${params.toString()}`);
      
      // Ensure requests is always an array
      const requestsData = response.data.data.requests || [];
      setRequests(requestsData);
      setTotalPages(response.data.data.pagination.total_pages);
      setError(null);
    } catch (err: any) {
      setError(`Failed to fetch requests: ${err.message}`);
      setRequests([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleStatusUpdate = async () => {
    if (!selectedRequest) return;
    
    const requestData = {
      status: newStatus,
    } as StatusUpdateRequest;
    
    console.log('Sending status update request:', requestData);
    console.log('Request URL:', `/api/inquiry/name-tag/${selectedRequest.id}/status`);
    
    try {
      await api.put(`/api/inquiry/name-tag/${selectedRequest.id}/status`, requestData);
      
      setStatusDialogOpen(false);
      fetchRequests(); // Refresh the list
    } catch (err: any) {
      console.error('Error updating status:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
      }
      setError('Failed to update status');
    }
  };

  const openStatusDialog = (request: AddNameTagRequest) => {
    console.log('Opening status dialog for request:', request);
    console.log('Current request status:', request.status);
    setSelectedRequest(request);
    setNewStatus(request.status);
    setStatusDialogOpen(true);
  };

  const openViewDialog = (request: AddNameTagRequest) => {
    setViewRequest(request);
    setViewDialogOpen(true);
  };

  const openEditDialog = (request: AddNameTagRequest) => {
    setEditRequest(request);
    setEditForm({
      name_tag: request.name_tag,
      website: request.website,
      category_label: request.category_label,
      description: request.description,
      comment: request.comment
    });
    setEditDialogOpen(true);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    setPage(1); // Reset to first page when submitting search
    fetchRequests();
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(1); // Reset to first page when filtering
  };

  const openApplyDialog = (request: AddNameTagRequest) => {
    setApplyRequest(request);
    setApplyDialogOpen(true);
  };

  const handleApplyConfirm = useCallback(async () => {
    if (!applyRequest) return;
    
    try {
      await api.post(`/api/inquiry/name-tag/${applyRequest.id}/apply`);
      setError(null);
      setApplyDialogOpen(false);
      // Show success notification
      setSuccessMessage('Name tag applied successfully! Request marked as completed.');
      setTimeout(() => setSuccessMessage(null), 5000); // Auto-hide after 5 seconds
      fetchRequests(); // Refresh the list
    } catch (err) {
      setError('Failed to apply name tag');
      console.error('Error applying name tag:', err);
    }
  }, [applyRequest, fetchRequests]);

  const handleApplyNameTag = async (requestId: number) => {
    try {
      await api.post(`/api/inquiry/name-tag/${requestId}/apply`);
      setError(null);
      // Show success message
      alert('Name tag applied successfully! Request marked as completed.');
      // Refresh the list to show updated status
      fetchRequests();
    } catch (err) {
      setError('Failed to apply name tag');
      console.error('Error applying name tag:', err);
    }
  };

  const openRevertDialog = (request: AddNameTagRequest) => {
    setRevertRequest(request);
    setRevertDialogOpen(true);
  };

  const handleRevertConfirm = useCallback(async () => {
    if (!revertRequest) return;
    
    try {
      await api.post(`/api/inquiry/name-tag/${revertRequest.id}/revert`);
      setError(null);
      setRevertDialogOpen(false);
      // Show success notification
      setSuccessMessage('Name tag reverted successfully! Request status updated.');
      setTimeout(() => setSuccessMessage(null), 5000); // Auto-hide after 5 seconds
      fetchRequests(); // Refresh the list
    } catch (err) {
      setError('Failed to revert name tag');
      console.error('Error reverting name tag:', err);
    }
  }, [revertRequest, fetchRequests]);

  const handleRevertNameTag = async (requestId: number) => {
    try {
      await api.post(`/api/inquiry/name-tag/${requestId}/revert`);
      setError(null);
      // Refresh the list to show updated status
      fetchRequests();
    } catch (err) {
      setError('Failed to revert name tag');
    }
  };

  const handleEditSubmit = async () => {
    if (!editRequest) return;
    
    try {
      await api.put(`/api/inquiry/name-tag/${editRequest.id}`, editForm);
      setError(null);
      setEditDialogOpen(false);
      // Refresh the list to show updated data
      fetchRequests();
    } catch (err) {
      setError('Failed to update request');
    }
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Add Name Tag Requests
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Manage requests for adding name tags to blockchain addresses. 
        Apply button will automatically mark requests as completed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          sx={{ flexGrow: 1 }}
          variant="outlined"
          placeholder="Search by name, email, address, or name tag..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <IconButton 
                onClick={handleSearchSubmit}
                size="small"
                sx={{ mr: 1 }}
                title="Search"
              >
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status Filter"
            startAdornment={<FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value={0}>Pending</MenuItem>
            <MenuItem value={1}>In Progress</MenuItem>
            <MenuItem value={2}>Completed</MenuItem>
            <MenuItem value={3}>Rejected</MenuItem>
            <MenuItem value={4}>On Hold</MenuItem>
            <MenuItem value={5}>Canceled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Requester</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Name Tag</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests && requests.length > 0 ? (
              requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.id}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {request.requester_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {request.requester_email_address}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {request.requester_company_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {request.requester_company_website}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                        <a 
                          href={`https://kaiascan.io/address/${request.address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', textDecoration: 'none' }}
                        >
                          {request.address}
                        </a>
                      </Typography>
                      <Chip 
                        label={addressOwnerLabels[request.is_address_owner as keyof typeof addressOwnerLabels]} 
                        size="small" 
                        variant="outlined"
                        color={request.is_address_owner === 0 ? 'success' : 'warning'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {request.name_tag}
                      </Typography>
                      {request.website && (
                        <Typography variant="caption" color="textSecondary">
                          <a 
                            href={request.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            {request.website}
                          </a>
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[request.status as keyof typeof statusLabels]?.label}
                      color={statusLabels[request.status as keyof typeof statusLabels]?.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(request.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => openViewDialog(request)} size="small" title="View Details">
                      <ViewIcon />
                    </IconButton>
                    <IconButton onClick={() => openEditDialog(request)} size="small" title="Edit Request">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => openStatusDialog(request)} size="small" title="Update Status">
                      <SettingsIcon />
                    </IconButton>
                    {(request.status === 0 || request.status === 1) && (
                      <IconButton 
                        onClick={() => openApplyDialog(request)} 
                        size="small" 
                        title="Apply Name Tag (will mark as completed)"
                        color="success"
                      >
                        <ApplyIcon />
                      </IconButton>
                    )}
                    {request.status === 2 && (
                      <IconButton 
                        onClick={() => openRevertDialog(request)} 
                        size="small" 
                        title="Revert Name Tag"
                        color="error"
                      >
                        <RevertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {loading ? 'Loading...' : 'No requests found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Request Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as number)}
              label="Status"
            >
              <MenuItem value={0}>Pending</MenuItem>
              <MenuItem value={1}>In Progress</MenuItem>
              <MenuItem value={2}>Completed</MenuItem>
              <MenuItem value={3}>Rejected</MenuItem>
              <MenuItem value={4}>On Hold</MenuItem>
              <MenuItem value={5}>Canceled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {viewRequest && (
            <Box>
              <Typography variant="h6" gutterBottom>Requester Information</Typography>
              <Typography><strong>Name:</strong> {viewRequest.requester_name}</Typography>
              <Typography><strong>Email:</strong> {viewRequest.requester_email_address}</Typography>
              <Typography><strong>Company:</strong> {viewRequest.requester_company_name}</Typography>
              <Typography><strong>Website:</strong> {viewRequest.requester_company_website}</Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Request Details</Typography>
              <Typography><strong>Address:</strong> <a href={`https://kaiascan.io/address/${viewRequest.address}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{viewRequest.address}</a></Typography>
              <Typography><strong>Name Tag:</strong> {viewRequest.name_tag}</Typography>
              <Typography><strong>Website:</strong> {viewRequest.website ? <a href={viewRequest.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{viewRequest.website}</a> : 'N/A'}</Typography>
              <Typography><strong>Category:</strong> {viewRequest.category_label || 'N/A'}</Typography>
              <Typography><strong>Description:</strong> {viewRequest.description || 'N/A'}</Typography>
              <Typography><strong>Comment:</strong> {viewRequest.comment || 'N/A'}</Typography>
              <Typography><strong>Discovered Links:</strong> {viewRequest.discovered_address_links || 'N/A'}</Typography>
              <Typography><strong>Address Owner:</strong> {addressOwnerLabels[viewRequest.is_address_owner as keyof typeof addressOwnerLabels]}</Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Status Information</Typography>
              <Typography><strong>Status:</strong> {statusLabels[viewRequest.status as keyof typeof statusLabels]?.label}</Typography>
              <Typography><strong>Created:</strong> {new Date(viewRequest.created_at).toLocaleString()}</Typography>
              <Typography><strong>Updated:</strong> {new Date(viewRequest.updated_at).toLocaleString()}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Request Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Name Tag Request</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name Tag"
                  value={editForm.name_tag}
                  onChange={(e) => handleEditFormChange('name_tag', e.target.value)}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={editForm.website}
                  onChange={(e) => handleEditFormChange('website', e.target.value)}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Category Label"
                  value={editForm.category_label}
                  onChange={(e) => handleEditFormChange('category_label', e.target.value)}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editForm.description}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comment"
                  value={editForm.comment}
                  onChange={(e) => handleEditFormChange('comment', e.target.value)}
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Confirmation Dialog */}
      <Dialog 
        open={applyDialogOpen} 
        onClose={() => setApplyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Name Tag Application</DialogTitle>
        <DialogContent>
          {applyRequest && (
            <Box>
              <Typography variant="h6" gutterBottom>Request Details</Typography>
              <Typography><strong>Address:</strong> <a href={`https://kaiascan.io/address/${applyRequest.address}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{applyRequest.address}</a></Typography>
              <Typography><strong>Name Tag:</strong> {applyRequest.name_tag}</Typography>
              <Typography><strong>Website:</strong> {applyRequest.website ? <a href={applyRequest.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{applyRequest.website}</a> : 'N/A'}</Typography>
              <Typography><strong>Category:</strong> {applyRequest.category_label || 'N/A'}</Typography>
              <Typography><strong>Description:</strong> {applyRequest.description || 'N/A'}</Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>⚠️ Action Confirmation</Typography>
              <Typography color="warning.main">
                This action will:
              </Typography>
              <ul>
                <li>Create/update the name tag in the system</li>
                <li>Mark this request as "Completed"</li>
                <li>Make the name tag visible to users</li>
              </ul>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Please review the information above before proceeding.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApplyConfirm} variant="contained" color="success">
            Apply Name Tag
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert Confirmation Dialog */}
      <Dialog 
        open={revertDialogOpen} 
        onClose={() => setRevertDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Name Tag Revert</DialogTitle>
        <DialogContent>
          {revertRequest && (
            <Box>
              <Typography variant="h6" gutterBottom>Request Details</Typography>
              <Typography><strong>Address:</strong> <a href={`https://kaiascan.io/address/${revertRequest.address}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{revertRequest.address}</a></Typography>
              <Typography><strong>Name Tag:</strong> {revertRequest.name_tag}</Typography>
              <Typography><strong>Website:</strong> {revertRequest.website ? <a href={revertRequest.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>{revertRequest.website}</a> : 'N/A'}</Typography>
              <Typography><strong>Category:</strong> {revertRequest.category_label || 'N/A'}</Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>⚠️ Action Confirmation</Typography>
              <Typography color="error.main">
                This action will:
              </Typography>
              <ul>
                <li>Remove the name tag from the system</li>
                <li>Reset this request status to "Pending"</li>
                <li>Make the name tag no longer visible to users</li>
              </ul>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Please confirm that you want to proceed with this action.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRevertConfirm} variant="contained" color="error">
            Revert Name Tag
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddNameTagTab;
